import type { HandHistoryAction as HandHistoryAction } from "@acepoker/poker-core";
import type { GameFlavor, SuggestedAction } from ".";
import type { Action } from "./action";

export type PlayerId = string;
export type Currency = number;

export type Player = {
  playerId: PlayerId;
  name: string;
  avatarId?: string;
  stack: number;
  host?: boolean;
  bot?: boolean;
  type?: string;
  states?: string[];
};

export type Seat = {
  playerId: PlayerId;
  stack: Currency;
  offline?: boolean;
  awayTimeout?: number;
};

export type Pot = {
  potId: string;
  amount: Currency;
};

export interface WinnerWithPayout {
  cards: string[];
  id: string;
  payout: number;
  seatNo: number;
}

export type HoleCards = Record<PlayerId, null | (string | null)[]>
export type HoleCardsValue = HoleCards[keyof HoleCards]


export type HandState = {
  holeCards: HoleCards;
  pots: Pot[];
  totalPot: number;
  bets: { [playerId: string]: Currency };
  foldedBets: number;
  dealerId: PlayerId;
  activePlayerId: PlayerId | null;
  communityCards: string[];
  minRaise: number;
  chipRange: {
    min: number;
    max: number;
  };
  raises: { amount: number; factor: number }[];
  legalActions: TableActionName[];
  winners: PlayerId[];
  winnerCards: string[];
  activeTimeout?: number;
};

export type Bets = HandState["bets"];

export type GameConfig = {
  blinds: Currency[];
  antes?: Currency;
  learningMode: boolean;
  assessmentMode: boolean;
  preConfiguredMode?: boolean;
  skipTo?: string | null;
};

export type JoinMessage = {
  type: "join";
  token: string;
  gameMode: "drill" | "compete";
};

export type ReadyMessage = {
  type: "ready";
};

export type RoomHistoryAction = Action & {
  createdAt: string;
  playerId: string;
  name: string;
};

export type RoomState = {
  name?: string;
  handNumber: number;
  hand: HandState | null;
  scores: Record<PlayerId, number>;
  seats: (Seat | null)[];
  players: Player[];
  ledger: (Pick<Player, "name" | "playerId" | "stack" | "bot"> & {
    net: number;
    handNet: number;
  })[];
  max_players: number;
  gameConfig: GameConfig | null;
  suggestedActions: Omit<SuggestedAction, "score">[];
  sessionId: string;
  handHistory: HandHistoryAction[];
  roomHistory: RoomHistoryAction[];
  playerTimeout?: number;
  hostId: string | null;
  flavors: GameFlavor[];
  isNewHandStarting?: boolean;

  passkey: string;
  chat: unknown[];
  joinRequests: Player[];
  strategyApiPayload: unknown;
};

export type FullMessage = {
  type: "full";
  playerId: PlayerId;
  state: RoomState;
};

interface Operation {
  op: "add" | "remove" | "replace" | "move" | "copy" | "test";
  from?: string;
  path?: string;
  value?: string;
}

export type DeltaMessage = {
  type: "delta";
  delta: Operation[];
};

export type ErrorMessage = {
  type: "error";
  message: string;
};

export type PongMessage = {
  type: "pong";
};

export type TableClosedMessage = {
  type: "table-closed";
  message?: string;
};

export type WebsocketMessage =
  | ReadyMessage
  | JoinMessage
  | FullMessage
  | DeltaMessage
  | ErrorMessage
  | PongMessage
  | TableClosedMessage;

export type JoinAction = {
  action: "join";
  token: string;
  playerId: string;
};

export type SitDownAction = {
  action: "sit-down";
  name: string;
  avatarId?: string;
  stack: number;
  seatNo?: number;
};

export type StandUpAction = {
  action: "stand-up";
};

export type FoldAction = {
  action: "fold";
  token?: string;
};

export type RaiseAction = {
  action: "raise";
  amount: number;
  token?: string;
};

export type BetAction = {
  action: "bet";
  amount: number;
  token?: string;
};

export type CallAction = {
  action: "call";
  token?: string;
};

export type CheckAction = {
  action: "check";
  token?: string;
};

export type PingAction = {
  action: "ping";
};

export type SetPlayerStackAction = {
  action: "set-player-stack";
  stack: number;
  playerId: string;
};

export type RequestGameStateAction = {
  action: "request-game-state";
};

export type LeaveTableAction = {
  action: "leave-table";
};

export type CloseTableAction = {
  action: "close-table";
};

export type SitInAction = {
  action: "sit-in";
};

export type SitOutAction = {
  action: "sit-out";
};

export type WebSocketAction =
  | JoinAction
  | SitDownAction
  | StandUpAction
  | FoldAction
  | RaiseAction
  | BetAction
  | CallAction
  | CheckAction
  | PingAction
  | SetPlayerStackAction
  | RequestGameStateAction
  | LeaveTableAction
  | CloseTableAction
  | SitInAction
  | SitOutAction;

export const PLAYER_ROLES_WITH_STRADDLES = {
  0: [],
  1: ["BTN"],
  2: ["BTN", "BB"],
  3: ["BTN", "SB", "BB"],
  4: ["BTN", "SB", "BB", "STR"],
  5: ["BTN", "SB", "BB", "STR", "UTG"],
  6: ["BTN", "SB", "BB", "STR", "UTG", "CO"],
  7: ["BTN", "SB", "BB", "STR", "UTG", "HJ", "CO"],
  8: ["BTN", "SB", "BB", "STR", "UTG", "UTG1", "HJ", "CO"],
  9: ["BTN", "SB", "BB", "STR", "UTG", "UTG1", "UTG2", "HJ", "CO"],
} as const;

export const PLAYER_ROLES = {
  0: [],
  1: ["BTN"],
  2: ["BTN", "BB"],
  3: ["BTN", "SB", "BB"],
  4: ["BTN", "SB", "BB", "UTG"],
  5: ["BTN", "SB", "BB", "UTG", "UTG1"],
  6: ["BTN", "SB", "BB", "UTG", "UTG1", "CO"],
  7: ["BTN", "SB", "BB", "UTG", "UTG1", "HJ", "CO"],
  8: ["BTN", "SB", "BB", "UTG", "UTG1", "UTG2", "HJ", "CO"],
  9: ["BTN", "SB", "BB", "UTG", "UTG1", "UTG2", "UTG3", "HJ", "CO"],
} as const;

export const BLINDS_ROLES = ["SB", "BB", "STR"];

export type PlayerPosition =
  | (typeof PLAYER_ROLES)[keyof typeof PLAYER_ROLES][number]
  | "STR";

export type TableFoldAction = {
  name: "fold";
};

export type TableCallAction = {
  name: "call";
};

export type TableCheckAction = {
  name: "check";
};

export type TableBetAction = {
  name: "bet";
  amount: number;
  raiseFactor: number;
};

export type TableRaiseAction = {
  name: "raise";
  amount: number;
  raiseFactor: number;
};

export type TableActionWithoutAmount =
  | TableFoldAction
  | TableCallAction
  | TableCheckAction;

export type TableActionWithAmount = TableBetAction | TableRaiseAction;

export type TableAction = TableActionWithoutAmount | TableActionWithAmount;

export type TableActionName = TableAction["name"];
